import React, { useEffect, useState } from "react";
import { Input, InputGroup, toaster } from "rsuite";
import {GetDevice_GetDeviceResponse} from "../../../../../../api";
import NotificationFactory from "../../../../../../shared/Notifications";
import { SensorBlockApi } from "../../../../../../services/SensorBlock.api";
import { SharedApi } from "../../../../../../services/Shared.api";
import {TfiSaveIcon} from "../../../../../../models/Icons.model";
import {SensorBlockInfoSectionProps} from "../SensorBlock.model";
import DeviceParameterSyncButton from "../../../../../shared/DeviceParameterSyncButton/DeviceParameterSyncButton";
import {ExternalDeviceType} from "../../../ExternalDevices.model";
import {ExternalDevicesUtils} from "../../../ExternalDevices.utils";

const SensorBlockInfoSection = ({ sensorId, isAdmin }: SensorBlockInfoSectionProps) => {
  const [loading, setLoading] = useState(true);
  const [sensorsData, setSensorsData] = useState<GetDevice_GetDeviceResponse | null>(null);

  useEffect(() => {
    getSensorsData().then();
  }, [sensorId]);

  const getSensorsData = async () => {
    setLoading(true);

    try {
      const result = await SensorBlockApi.getSensorBlockData(sensorId as string);
      setSensorsData(result);
    } catch (error) {
      toaster.push(
        NotificationFactory.error("Ошибка"),
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  const onChangeArticle = (value: string) => {
    setSensorsData((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        article: value
      };
    });
  };

  const onChangeModel = (value: string) => {
    setSensorsData((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        model: value
      };
    });
  };

  const onChangePlace = (value: string) => {
    setSensorsData((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        place: value
      };
    });
  };

  const onSaveArticle = async () => {
    setLoading(true);
    try {
      await SharedApi.updateArticle(sensorId as string, sensorsData?.article ?? "");
      toaster.push(
        NotificationFactory.success("Артикул обновлен"),
        { placement: "topEnd" }
      );
      await getSensorsData().then();
    } catch (e) {
      console.error("Ошибка при сохранении артикула:", e);
      toaster.push(
        NotificationFactory.error("Не удалось обновить артикул"),
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  const onSaveModel = async () => {
    setLoading(true);
    try {
      await SharedApi.updateModel(sensorId as string, sensorsData?.model ?? "");
      toaster.push(
        NotificationFactory.success("Модель обновлена"),
        { placement: "topEnd" }
      );
      await getSensorsData().then();
    } catch (e) {
      console.error("Ошибка при сохранении модели:", e);
      toaster.push(
        NotificationFactory.error("Ошибка при сохранении модели"),
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  const onSavePlace = async () => {
    setLoading(true);
    try {
      await SharedApi.updatePlace(sensorId as string, sensorsData?.place ?? "");
      toaster.push(
        NotificationFactory.success("Место установки обновлено"),
        { placement: "topEnd" }
      );
      await getSensorsData().then();
    } catch (e) {
      console.error("Ошибка при сохранении места:", e);
      toaster.push(
        NotificationFactory.error("Ошибка при сохранении места"),
        { placement: "topEnd" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section>
        <div className="row align-items-center">
          <div className="col-md-12">
            <div className="mb-2">
              <InputGroup>
                <InputGroup.Addon>Артикул</InputGroup.Addon>
                <Input
                  size="md"
                  value={sensorsData?.article || ""}
                  onChange={onChangeArticle}
                />
                {isAdmin && (
                  <InputGroup.Addon>
                    <TfiSaveIcon
                      style={{ cursor: "pointer" }}
                      onClick={onSaveArticle}
                      className={loading ? "loading" : ""}
                    />
                  </InputGroup.Addon>
                )}
              </InputGroup>
            </div>

            <div className="mb-2">
              <InputGroup>
                <InputGroup.Addon>Дата производства</InputGroup.Addon>
                <Input
                  size="md"
                  value={sensorsData?.fabricDate || ""}
                />
              </InputGroup>
            </div>

            <div className="mb-2">
              <InputGroup>
                <InputGroup.Addon>Версия прошивки</InputGroup.Addon>
                <Input
                  size="md"
                  value={sensorsData?.firmwareVersion || ""}
                />
                {isAdmin && (
                  <DeviceParameterSyncButton
                    deviceId={sensorId}
                    parameter="FirmwareVersion"
                    onSynced={getSensorsData}
                  />
                )}
              </InputGroup>
            </div>
          </div>

          <div className="col-md-12">
            <div className="mb-2">
              <InputGroup>
                <InputGroup.Addon>Модель</InputGroup.Addon>
                <Input
                  size="md"
                  value={sensorsData?.model || ""}
                  onChange={onChangeModel}
                />
                {isAdmin && (
                  <InputGroup.Addon>
                    <TfiSaveIcon
                      style={{ cursor: "pointer" }}
                      onClick={onSaveModel}
                      className={loading ? "loading" : ""}
                    />
                  </InputGroup.Addon>
                )}
              </InputGroup>
            </div>
            <div className="mb-2">
              <InputGroup>
                <InputGroup.Addon>Серийный номер</InputGroup.Addon>
                <Input
                  size="md"
                  value={ExternalDevicesUtils.formatDeviceSerialNumber(sensorsData?.serialNumber, ExternalDeviceType.SensorBlockT)}
                />
              </InputGroup>
            </div>
            <div className="mb-2">
              <InputGroup>
                <InputGroup.Addon>Место установки</InputGroup.Addon>
                <Input
                  size="md"
                  value={sensorsData?.place || ""}
                  onChange={onChangePlace}
                />
                {isAdmin && (
                  <DeviceParameterSyncButton
                    deviceId={sensorId}
                    parameter="Place"
                    onSynced={getSensorsData}
                  />
                )}
                <InputGroup.Addon>
                  <TfiSaveIcon
                    style={{ cursor: "pointer" }}
                    onClick={onSavePlace}
                    className={loading ? "loading" : ""}
                  />
                </InputGroup.Addon>
              </InputGroup>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SensorBlockInfoSection;
